import { daysBetween, type IsoDate } from './calendar';
import { lateFee, type TurnoverBand } from './late-fees';
import { assertPaise, type Paise, percentOf } from './money';
import {
  addHeads,
  type ByHead,
  byHead,
  interestByHead,
  NO_TAX,
  type SetOff,
  setOff,
  sumHeads,
  TAX_HEADS,
} from './setoff';

/**
 * GSTR-3B for a month (Phase 10): tax on sales as GSTR-1 put it (locked on the portal), credit from
 * GSTR-2B, the two things the person adds (reverse-charge purchases, Table 3.1(d) and 4A(3); credit to
 * reverse, Table 4B), and what that leaves to pay.
 *
 * Interest and late fee: the portal works out a late GSTR-3B's interest and late fee once it's filed
 * and collects them in the next month's GSTR-3B (Table 5.1, "for the previous tax period"; GSTN's
 * advisory of March 2026). So what's paid now is this month's tax in cash plus the previous month's
 * interest and late fee as the portal drafted them; this month's own are an estimate, for next month.
 */

/** Reverse charge a small business usually meets (section 9(3)). */
export const REVERSE_CHARGE_KINDS = ['gta', 'legal', 'rent', 'import_services'] as const;
export type ReverseChargeKind = (typeof REVERSE_CHARGE_KINDS)[number];

/**
 * The rate each is taxed at, and whether it's always IGST: goods transport by a GTA that doesn't
 * charge GST itself, 5% (Notification 13/2017-CTR); an advocate's services, 18% (13/2017-CTR);
 * renting commercial property from an unregistered owner, 18% (09/2024-CTR, from 10 October 2024);
 * services from abroad, IGST at 18% (10/2017-IT(R)).
 */
export const REVERSE_CHARGE_RATES: Readonly<Record<ReverseChargeKind, { rate: number; igstOnly: boolean }>> = {
  gta: { rate: 5, igstOnly: false },
  legal: { rate: 18, igstOnly: false },
  rent: { rate: 18, igstOnly: false },
  import_services: { rate: 18, igstOnly: true },
};

export interface ReverseChargeEntry {
  kind: ReverseChargeKind;
  /** Before tax. */
  taxableValue: Paise;
  /** The supplier is in another state (IGST); otherwise CGST + SGST. Services from abroad are always IGST. */
  interState?: boolean;
  supplier?: string | null;
}

/** The tax on one reverse-charge purchase, by head (split evenly between CGST and SGST, odd paisa to CGST). */
export function reverseChargeTax(entry: ReverseChargeEntry): ByHead {
  assertPaise(entry.taxableValue, 'taxableValue');
  if (entry.taxableValue < 0) throw new RangeError('taxableValue must not be negative');
  const { rate, igstOnly } = REVERSE_CHARGE_RATES[entry.kind];
  if (igstOnly || entry.interState) return byHead({ igst: percentOf(entry.taxableValue, rate) });
  const half = percentOf(entry.taxableValue, rate / 2);
  return byHead({ cgst: half, sgst: half });
}

/** Why credit is reversed: Table 4B(1) for good (17(5), personal use, rules 42/43), 4B(2) to claim back later (rule 37: supplier not paid in 180 days). */
export const REVERSAL_REASONS = ['blocked', 'personal', 'exempt', 'unpaid_180_days', 'rule_37a'] as const;
export type ReversalReason = (typeof REVERSAL_REASONS)[number];
export const PERMANENT_REVERSALS: ReadonlySet<ReversalReason> = new Set(['blocked', 'personal', 'exempt']);

export interface Reversal {
  reason: ReversalReason;
  amounts: ByHead;
}

export interface CashLedger {
  tax: ByHead;
  interest: ByHead;
  fee: ByHead;
}

export interface Gstr3bInput {
  /** Tax on sales, by head, from GSTR-1 (Table 3.1(a); locked on the portal). */
  sales: ByHead;
  /** Credit GSTR-2B says can be claimed (Table 4A(5) and the other 4A lines), by head. */
  credit: ByHead;
  reverseCharge?: readonly ReverseChargeEntry[];
  reversals?: readonly Reversal[];
  /** Interest and late fee for the previous month, as the portal drafted them in Table 5.1. */
  previous?: { interest: ByHead; lateFee: ByHead };
  /** The electronic cash ledger's balance, by minor head. */
  cash?: CashLedger;
  /** For this month's own interest and late fee (collected next month): its due date and the day it's filed. */
  dueOn?: IsoDate;
  filingOn?: IsoDate;
  turnoverBand?: TurnoverBand;
}

export interface Gstr3bComputation {
  sales: ByHead;
  /** Table 3.1(d): tax on reverse-charge purchases, paid in cash. */
  reverseCharge: ByHead;
  credit: {
    /** Table 4A: from GSTR-2B, plus the reverse-charge tax paid this month (4A(3)). */
    available: ByHead;
    /** Table 4B(1) and 4B(2). */
    reversedForGood: ByHead;
    reversedForNow: ByHead;
    /** Table 4C: what can be used. */
    net: ByHead;
  };
  setOff: SetOff;
  /** Table 5.1: the previous month's interest and late fee, paid in cash. */
  previous: { interest: ByHead; lateFee: ByHead };
  /** What the cash ledger covers, and what's left to pay by challan, by minor head. */
  fromLedger: CashLedger;
  toPay: CashLedger;
  totals: {
    /** Tax on sales plus reverse charge. */
    tax: Paise;
    creditUsed: Paise;
    /** Previous month's interest and late fee. */
    previous: Paise;
    fromLedger: Paise;
    toPay: Paise;
  };
  /** This month filed late: its own interest and late fee so far, which the portal collects next month. */
  late: { days: number; interest: ByHead; lateFee: ByHead } | null;
}

const minHeads = (a: ByHead, b: ByHead): ByHead =>
  byHead({
    igst: Math.min(a.igst, b.igst),
    cgst: Math.min(a.cgst, b.cgst),
    sgst: Math.min(a.sgst, b.sgst),
    cess: Math.min(a.cess, b.cess),
  });
const minusHeads = (a: ByHead, b: ByHead): ByHead =>
  byHead({ igst: a.igst - b.igst, cgst: a.cgst - b.cgst, sgst: a.sgst - b.sgst, cess: a.cess - b.cess });

/** Credit reversed is more than there is. */
export class ReversalTooLargeError extends Error {
  constructor(readonly head: (typeof TAX_HEADS)[number]) {
    super(`More ${head.toUpperCase()} credit reversed than there is`);
  }
}

export function gstr3b(input: Gstr3bInput): Gstr3bComputation {
  const reverseCharge = addHeads(...(input.reverseCharge ?? []).map(reverseChargeTax));
  const available = addHeads(input.credit, reverseCharge);
  const reversals = input.reversals ?? [];
  const reversedForGood = addHeads(...reversals.filter((r) => PERMANENT_REVERSALS.has(r.reason)).map((r) => r.amounts));
  const reversedForNow = addHeads(...reversals.filter((r) => !PERMANENT_REVERSALS.has(r.reason)).map((r) => r.amounts));
  const reversed = addHeads(reversedForGood, reversedForNow);
  for (const head of TAX_HEADS) if (reversed[head] > available[head]) throw new ReversalTooLargeError(head);
  const net = minusHeads(available, reversed);
  const cash = input.cash ?? { tax: NO_TAX, interest: NO_TAX, fee: NO_TAX };
  const done = setOff({ liability: input.sales, credit: net, reverseCharge, cash: cash.tax });
  const previous = input.previous ?? { interest: NO_TAX, lateFee: NO_TAX };
  const interestFromLedger = minHeads(previous.interest, cash.interest);
  const feeFromLedger = minHeads(previous.lateFee, cash.fee);
  const fromLedger = { tax: done.fromLedger, interest: interestFromLedger, fee: feeFromLedger };
  const toPay = {
    tax: done.toPay,
    interest: minusHeads(previous.interest, interestFromLedger),
    fee: minusHeads(previous.lateFee, feeFromLedger),
  };
  const days = input.dueOn && input.filingOn ? Math.max(0, daysBetween(input.dueOn, input.filingOn)) : 0;
  const late =
    days > 0
      ? {
          days,
          interest: interestByHead({ cashTax: done.cashTax, days }),
          lateFee: splitFee(
            lateFee({
              form: 'gstr3b',
              daysLate: days,
              nil: sumHeads(input.sales) === 0 && sumHeads(reverseCharge) === 0,
              ...(input.turnoverBand ? { turnoverBand: input.turnoverBand } : {}),
            }),
          ),
        }
      : null;
  return {
    sales: input.sales,
    reverseCharge,
    credit: { available, reversedForGood, reversedForNow, net },
    setOff: done,
    previous,
    fromLedger,
    toPay,
    totals: {
      tax: sumHeads(input.sales) + sumHeads(reverseCharge),
      creditUsed: sumHeads(done.creditUsed),
      previous: sumHeads(previous.interest) + sumHeads(previous.lateFee),
      fromLedger: sumHeads(fromLedger.tax) + sumHeads(fromLedger.interest) + sumHeads(fromLedger.fee),
      toPay: sumHeads(toPay.tax) + sumHeads(toPay.interest) + sumHeads(toPay.fee),
    },
    late,
  };
}

/** A late fee is half CGST, half SGST (there's no IGST late fee). */
export function splitFee(total: Paise): ByHead {
  const half = Math.floor(total / 2);
  return byHead({ cgst: total - half, sgst: half });
}
